class LandingPageStickyAddToCart extends HTMLElement {
  constructor() {
    super();
    /** Max quantity to add to cart */
    this.maxQuantity = 10;

    this.onQuantityControlsClick = this.quantityControlsEventHandler.bind(this);
    this.onQuantityInput = this.quantityInputEventHandler.bind(this);
    this.onAddToCartClick = this.addToCartEventHandler.bind(this);
    this.onIntersectionChange = this.handleIntersectionChange.bind(this);
    this.onMediaQueryChange = this.handleMediaQueryChange.bind(this);
  }

  // Called when the element is connected to the DOM.
  connectedCallback() {
    this.section = this;
    this.hostSection = this.closest(".landing-page--sticky-add-to-cart") || this;

    this.quantityControls = this.section.querySelector(
      "#sticky-add-to-cart--quantity"
    );
    this.quantityInput = this.quantityControls?.querySelector(
      `input[type="number"]`
    );

    this.addToCartBtn = this.section.querySelector("#sticky-add-to-cart-btn");

    if (this.quantityControls) {
      this.quantityControls.addEventListener(
        "click",
        this.onQuantityControlsClick
      );
      this.quantityControls.addEventListener("input", this.onQuantityInput);
    }

    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener("click", this.onAddToCartClick);
    }

    this.setStickyVisibility(false);
    this.initVisibilityControls();
  }

  // Called when the element is disconnected from the DOM.
  disconnectedCallback() {
    if (this.quantityControls) {
      this.quantityControls.removeEventListener(
        "click",
        this.onQuantityControlsClick
      );
      this.quantityControls.removeEventListener("input", this.onQuantityInput);
    }

    if (this.addToCartBtn) {
      this.addToCartBtn.removeEventListener("click", this.onAddToCartClick);
    }

    this.teardownVisibilityControls();
  }

  /**
   * Handles click events on quantity controls.
   * @param {Event} event - The click event.
   */
  quantityControlsEventHandler(event) {
    const btn = event.target.closest("button");
    if (!btn) return;
    const { stickyAddToCartQuantityAction } = btn.dataset;
    this.updateQuantity(stickyAddToCartQuantityAction);
  }

  /**
   * Handles input events on quantity input.
   * @param {Event} event - The input event.
   */
  quantityInputEventHandler(event) {
    if (!this.quantityInput) return;
    const { value } = event.target;
    if (value > this.maxQuantity) this.quantityInput.value = 10;
  }

  /**
   * Handles click events on the add to cart button.
   * @param {Event} event - The click event.
   */
  addToCartEventHandler(event) {
    const btn = event.target.closest("#sticky-add-to-cart-btn");
    if (!btn) return;
    const { variantId } = btn.dataset;
    this.addToCart(variantId);
  }

  /**
   * Updates the quantity based on the provided action.
   * @param {string} action - The action to perform ('plus' or 'minus').
   */
  updateQuantity(action) {
    if (!this.quantityInput) return;

    let currentValue = parseInt(this.quantityInput.value, 10);

    if (Number.isNaN(currentValue)) {
      currentValue = 1;
      this.quantityInput.value = currentValue;
    }

    if (action === "plus" && currentValue < this.maxQuantity) {
      this.quantityInput.value = currentValue + 1;
    }

    if (action === "minus" && currentValue > 1) {
      this.quantityInput.value = currentValue - 1;
    }
  }

  /**
   * Adds an item to the cart.
   * @param {string} id - The ID of the item to add.
   */
  async addToCart(id) {
    const quantity = this.quantityInput
      ? Math.max(1, Math.min(this.maxQuantity, +this.quantityInput.value || 1))
      : 1;

    const formData = {
      items: [
        {
          id,
          quantity,
        },
      ],
    };

    try {
      await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
    } catch (error) {
      console.log(`❌ LP Sticky Add to Cart | addToCart | ${error}`);
    }
  }

  initVisibilityControls() {
    if (!this.hostSection) return;

    if (!window.matchMedia) {
      this.setStickyVisibility(true);
      return;
    }

    this.mobileMediaQuery = window.matchMedia("(max-width: 768px)");

    if (this.mobileMediaQuery.addEventListener) {
      this.mobileMediaQuery.addEventListener("change", this.onMediaQueryChange);
    } else if (this.mobileMediaQuery.addListener) {
      this.mobileMediaQuery.addListener(this.onMediaQueryChange);
    }

    this.handleMediaQueryChange(this.mobileMediaQuery.matches);
  }

  teardownVisibilityControls() {
    this.detachIntersectionObserver();

    if (this.mobileMediaQuery) {
      if (this.mobileMediaQuery.removeEventListener) {
        this.mobileMediaQuery.removeEventListener(
          "change",
          this.onMediaQueryChange
        );
      } else if (this.mobileMediaQuery.removeListener) {
        this.mobileMediaQuery.removeListener(this.onMediaQueryChange);
      }
    }

    this.mobileMediaQuery = undefined;
  }

  handleMediaQueryChange(eventOrMatches) {
    const matches =
      typeof eventOrMatches === "boolean"
        ? eventOrMatches
        : Boolean(eventOrMatches?.matches);

    if (matches) {
      this.attachIntersectionObserver();
    } else {
      this.detachIntersectionObserver();
      this.setStickyVisibility(true);
    }
  }

  attachIntersectionObserver() {
    if (this.intersectionObserver || !("IntersectionObserver" in window)) {
      if (!("IntersectionObserver" in window)) {
        this.setStickyVisibility(true);
      }
      return;
    }

    this.visibilityTarget =
      document.querySelector("#pdp-product-overview-buy-quantity-controllers") ||
      document.querySelector("#pdp-product-overview-add-to-bag");

    if (!this.visibilityTarget) {
      this.setStickyVisibility(true);
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      this.onIntersectionChange,
      {
        threshold: 0.1,
      }
    );

    this.intersectionObserver.observe(this.visibilityTarget);
    this.updateStickyVisibilityFromTarget();
  }

  detachIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.intersectionObserver = undefined;
    this.visibilityTarget = undefined;
  }

  handleIntersectionChange(entries) {
    if (!Array.isArray(entries)) return;

    const entry = entries.find((item) => item.target === this.visibilityTarget);

    if (!entry) return;

    this.setStickyVisibility(!(entry.isIntersecting && entry.intersectionRatio > 0));
  }

  updateStickyVisibilityFromTarget() {
    if (!this.visibilityTarget) {
      this.setStickyVisibility(true);
      return;
    }

    const rect = this.visibilityTarget.getBoundingClientRect();
    const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;

    this.setStickyVisibility(!isVisible);
  }

  setStickyVisibility(shouldShow) {
    if (!this.hostSection) return;

    if (this.isStickyVisible === shouldShow) return;

    this.isStickyVisible = shouldShow;
    const visibilityValue = shouldShow ? "true" : "false";

    this.hostSection.setAttribute("data-sticky-visible", visibilityValue);
    this.hostSection.setAttribute("aria-hidden", shouldShow ? "false" : "true");

    this.setAttribute("data-sticky-visible", visibilityValue);
    this.setAttribute("aria-hidden", shouldShow ? "false" : "true");
  }
}

customElements.define(
  "landing-page-sticky-add-to-cart",
  LandingPageStickyAddToCart
);
